import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  CircleCheckIcon,
  InfoIcon,
  Loader2Icon,
  OctagonXIcon,
  TriangleAlertIcon,
} from 'lucide-react';
import { Toaster as Sonner, type ToasterProps } from 'sonner';

import { useShadowRoot, usePortalContainer } from '@/api/evo';

/**
 * Mirrors any `<style>` element in `document.head` whose textContent
 * includes `selector` into `shadowRoot`. Sonner injects its toast styles
 * into `document.head` at runtime; since we mount inside an Evo shadow
 * root, those styles never reach the toasts without this mirror.
 *
 * EDS-provided pattern (`/* MFE Shadow Dom Fix ... *\/` block in the
 * generated `@eds/sonner` source). Hoisted out of the comment with the
 * import rewired to this repo's `useShadowRoot` (which lives in
 * `evo/api`, not the `@/hooks/use-shadow-root` path EDS's template
 * assumes).
 */
export function addStylesToShadowDOM(shadowRoot: ShadowRoot, selector: string): () => void {
  function isStyleAlreadyInShadowDOM(styleContent: string | null): boolean {
    if (!styleContent) return false;
    const existing = Array.from(shadowRoot.querySelectorAll('style'));
    return existing.some((el) => el.textContent === styleContent);
  }

  function addStyleToShadowDOM(styleElement: HTMLStyleElement): void {
    if (!isStyleAlreadyInShadowDOM(styleElement.textContent)) {
      const cloned = styleElement.cloneNode(true) as HTMLStyleElement;
      shadowRoot.appendChild(cloned);
    }
  }

  function processStyleElement(element: HTMLStyleElement): void {
    if (element.textContent?.includes(selector)) {
      addStyleToShadowDOM(element);
    }
  }

  function addMatchingStyles() {
    const styleElements = document.head.querySelectorAll('style');
    for (const styleEl of styleElements) {
      processStyleElement(styleEl);
    }
  }

  function handleAddedNode(node: Node): void {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as Element;
      if (element.tagName === 'STYLE') {
        processStyleElement(element as HTMLStyleElement);
      }
    }
  }

  function handleMutations(mutations: MutationRecord[]): void {
    for (const mutation of mutations) {
      if (mutation.type === 'childList') {
        for (const node of mutation.addedNodes) {
          handleAddedNode(node);
        }
      }
    }
  }

  addMatchingStyles();

  const observer = new MutationObserver(handleMutations);
  observer.observe(document.head, { childList: true, subtree: true });

  return () => {
    observer.disconnect();
  };
}

/**
 * Subscribes to `document.head` style insertions and re-injects any whose
 * textContent matches `selector` into the active shadow root. Cleans up
 * its MutationObserver on unmount or when `shadowRoot` / `selector` change.
 */
function useShadowStyles(shadowRoot: ShadowRoot | null, selector: string): void {
  const cleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!shadowRoot) return;

    if (cleanupRef.current) {
      cleanupRef.current();
    }

    cleanupRef.current = addStylesToShadowDOM(shadowRoot, selector);

    return () => {
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
      }
    };
  }, [shadowRoot, selector]);
}

/**
 * EDS-flavoured `<Toaster />` — wraps `sonner` with EDS icons, theme
 * tokens, and shadow-DOM style mirroring so toasts render correctly
 * inside an Evo MFE host. Mount once near the root of the app.
 *
 * Sonner positions itself with `position: fixed` and (unlike our Radix-based
 * modals/popovers) never portals itself to `document.body` — see
 * https://github.com/emilkowalski/sonner, it just renders inline wherever
 * it's mounted. A `fixed` element is positioned relative to the nearest
 * ancestor that establishes a containing block (any `transform`, `filter`,
 * `perspective`, `contain`, etc.), not necessarily the viewport. Embedded in
 * a host app's own layout (e.g. WFM), `<Toaster />` is very likely to sit
 * beneath such an ancestor, so its toasts render clipped/mispositioned or
 * simply invisible instead of floating over the whole page. We fix that by
 * portalling it ourselves, using the same `usePortalContainer` container our
 * Radix-based components use (falling back to `document.body`).
 *
 * Theme is hardcoded to `light`: sonner's own dark-mode CSS hardcodes
 * several colours (e.g. `[data-description]`) rather than deriving them
 * from our `--normal-*` custom properties, so following the host's/OS's
 * dark-mode preference via `next-themes` produced toast text that didn't
 * match the rest of our (light-token-driven) UI and was hard to read. We
 * don't currently theme the rest of the app to dark either, so this keeps
 * toasts consistent without pulling in a `next-themes` dependency.
 */
function Toaster({ ...props }: ToasterProps) {
  const shadowRoot = useShadowRoot();
  const portalContainer = usePortalContainer();
  useShadowStyles(shadowRoot, '[data-sonner-toaster]');

  return createPortal(
    <Sonner
      theme="light"
      className="toaster group"
      icons={{
        success: <CircleCheckIcon className="size-4" />,
        info: <InfoIcon className="size-4" />,
        warning: <TriangleAlertIcon className="size-4" />,
        error: <OctagonXIcon className="size-4" />,
        loading: <Loader2Icon className="size-4 animate-spin" />,
      }}
      style={
        {
          '--normal-bg': 'var(--popover)',
          '--normal-text': 'var(--popover-foreground)',
          '--normal-border': 'var(--border)',
          '--border-radius': 'var(--radius)',
        } as React.CSSProperties
      }
      {...props}
    />,
    portalContainer ?? document.body,
  );
}

export { Toaster };
